import { useEffect, useRef, useState } from "react";
import { useTutorial } from "../../context/tutorialContext";

function TutorialTooltip({ targetRect }) {
    const { currentStep, currentIndex, totalSteps, progress, next, back, skip, dismiss, singleStepMode } = useTutorial();
    const tooltipRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (isMobile || !targetRect || !tooltipRef.current) return;

        const tooltip = tooltipRef.current.getBoundingClientRect();
        const placement = currentStep?.placement || "bottom";
        const gap = 12;

        let top = 0;
        let left = 0; 

        switch (placement) {
            case "top":
                top = targetRect.top - tooltip.height - gap;
                left = targetRect.left + targetRect.width / 2 - tooltip.width / 2;
                break;
            case "bottom":
                top = targetRect.top + targetRect.height + gap;
                left = targetRect.left + targetRect.width / 2 - tooltip.width / 2;
                break;
            case "left":
                top = targetRect.top + targetRect.height / 2 - tooltip.height / 2;
                left = targetRect.left - tooltip.width - gap;
                break;
            case "right":
                top = targetRect.top + targetRect.height / 2 - tooltip.height / 2;
                left = targetRect.left + targetRect.width + gap;
                break;
            default:
                top = targetRect.top + targetRect.height + gap;
                left = targetRect.left + targetRect.width / 2 - tooltip.width / 2;
        }

        const maxLeft = window.innerWidth - tooltip.width - 16;
        const maxTop = window.innerHeight - tooltip.height - 16;
        left = Math.max(16, Math.min(left, maxLeft));
        top = Math.max(16, Math.min(top, maxTop));

        setPosition({ top, left });
    }, [targetRect, currentStep, isMobile]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") dismiss();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [dismiss]);

    useEffect(() => {
        if (!tooltipRef.current) return;
        const focusable = tooltipRef.current.querySelector("button");
        if (focusable) focusable.focus();
    }, [currentIndex]);

    if (!currentStep) return null;

    const stepNumber = currentIndex + 1;
    const isFirst = currentIndex === 0;

    return (
        <div
            ref={tooltipRef}
            className={`ui-tutorial-tooltip ${isMobile ? "ui-tutorial-tooltip--mobile" : ""}`}
            style={isMobile ? {} : { top: position.top, left: position.left }}
            role="dialog"
            aria-modal="true"
            aria-label={currentStep.title}
        >
            <div className="ui-tutorial-tooltip__header">
                <h3 className="ui-tutorial-tooltip__title">{currentStep.title}</h3>
                {!singleStepMode && (
                    <span className="ui-tutorial-tooltip__counter" aria-live="polite">
                        Step {stepNumber} of {totalSteps}
                    </span>
                )}
            </div>

            <p className="ui-tutorial-tooltip__description">{currentStep.description}</p>

            {!singleStepMode && (
                <div className="ui-tutorial-tooltip__progress">
                    <div
                        className="ui-tutorial-tooltip__progress-bar"
                        style={{ width: `${progress * 100}%` }}
                    />
                </div>
            )}

            <div className="ui-tutorial-tooltip__actions">
                <button
                    className="ui-tutorial-tooltip__btn ui-tutorial-tooltip__btn--skip"
                    onClick={singleStepMode ? dismiss : skip}
                >
                    {singleStepMode ? "Close" : "Skip Tutorial"}
                </button>
                <div className="ui-tutorial-tooltip__nav">
                    {!singleStepMode && !isFirst && (
                        <button
                            className="ui-tutorial-tooltip__btn ui-tutorial-tooltip__btn--back"
                            onClick={back}
                        >
                            Back
                        </button>
                    )}
                    <button
                        className="ui-tutorial-tooltip__btn ui-tutorial-tooltip__btn--next"
                        onClick={next}
                    >
                        {singleStepMode ? "Got It" : stepNumber === totalSteps ? "Finish" : "Next"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default TutorialTooltip;
